import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import {
  BotanicalCorner,
  GoldLineDivider,
  MandalaOrnament,
  TattooPathDrawing,
  FlowingWaves,
} from '@/components/feature/DrawingLines';
import BrandStorySection from './components/BrandStorySection';

const milestones = [
  { year: '2018', title: '선율 창립', desc: '서울 청담동에서 1인 프리미엄 타투 스튜디오로 시작. 라인워크 전문 브랜드로 포지셔닝.' },
  { year: '2020', title: '브랜드 아이덴티티 확립', desc: '\'Where Lines Become Art\' 슬로건 론칭. Medical Grade 위생 시스템 자체 개발 완료.' },
  { year: '2021', title: '아카데미 오픈', desc: '선율 아카데미 설립. 첫 기수 아티스트 6명 배출. 체계적 교육 커리큘럼 완성.' },
  { year: '2022', title: '프랜차이즈 론칭', desc: '가맹 사업 공식 시작. 첫 해 10개 가맹점 오픈. 전국 확장 기반 마련.' },
  { year: '2023', title: '전국 30호점 돌파', desc: '수도권을 넘어 부산·대구·광주 등 전국 주요 도시 진출. 누적 시술 고객 5만 명 달성.' },
  { year: '2024', title: '하이엔드 갤러리 모델 출시', desc: '프리미엄형(Gallery) 인테리어 패키지 론칭. 플래그십 스토어 청담 오픈.' },
];

const values = [
  {
    icon: 'ri-heart-line',
    title: '고객 중심',
    desc: '모든 결정의 기준은 고객입니다. 시술 전 충분한 상담, 시술 중 안전, 시술 후 케어까지 고객 경험 전체를 설계합니다.',
  },
  {
    icon: 'ri-palette-line',
    title: '예술적 완성도',
    desc: '타투는 영구적인 예술입니다. 선율의 아티스트는 단순한 시술자가 아닌, 고객의 이야기를 피부 위에 새기는 예술가입니다.',
  },
  {
    icon: 'ri-shield-check-line',
    title: '타협 없는 안전',
    desc: '아름다움과 안전은 공존합니다. 의료 수준의 위생 기준을 모든 가맹점에 동일하게 적용하여 고객을 보호합니다.',
  },
  {
    icon: 'ri-team-line',
    title: '함께 성장',
    desc: '가맹점주, 아티스트, 고객 모두가 함께 성장하는 생태계를 만듭니다. 선율의 성공은 모두의 성공입니다.',
  },
];

function AboutHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  return (
    <section className="relative pt-32 sm:pt-36 md:pt-40 pb-16 sm:pb-20 md:pb-24 bg-[#1A1A1A] overflow-hidden">
      <div className="absolute inset-0 opacity-50">
        <img
          src="https://public.readdy.ai/ai/img_res/97cbb5e2929d2e8c2a5e7645aa0a7555.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-transparent to-[#1A1A1A]/60" />
      <FlowingWaves color="rgba(201,169,110,0.1)" lineCount={4} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.2)" size={160} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 md:px-16 text-center">
        <div className="flex items-center justify-center gap-2 mb-8 sm:mb-10"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out' }}>
          <Link to="/brand" className="text-[10px] tracking-[0.3em] uppercase text-white/30 hover:text-white/60 transition-colors cursor-pointer">Brand</Link>
          <span className="text-white/20 text-xs">/</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E]">브랜드 소개</span>
        </div>
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out 0.15s' }}>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
            브랜드 소개
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-white/50 leading-relaxed max-w-xs sm:max-w-lg mx-auto">
            선율이 걸어온 길, 그리고 우리가 추구하는 가치를 소개합니다.
          </p>
        </div>
        <div className="mx-auto mt-10 sm:mt-12 h-px bg-white/15 transition-all duration-1000 ease-out" style={{ width: visible ? '80px' : '0px', transitionDelay: '500ms' }} />
      </div>
    </section>
  );
}

function PhilosophySection() {
  const { ref, visible } = useScrollAnimation(0.1);
  return (
    <section className="py-16 sm:py-24 md:py-36 bg-white overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-left" color="rgba(201,169,110,0.1)" size={160} />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-16">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-40px)', transition: 'all 1s ease-out' }}>
            <p className="text-[9px] sm:text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-4 sm:mb-5">Our Philosophy</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#1A1A1A] mb-6 sm:mb-8 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
              선 하나에서 시작되는<br />
              <span className="text-[#AAAAAA]">당신의 이야기</span>
            </h2>
            <div className="space-y-4 sm:space-y-5 text-sm text-[#666] leading-relaxed">
              <p>타투는 단순한 장식이 아닙니다. 그것은 한 사람의 이야기이자, 기억이며, 정체성의 표현입니다. 선율은 모든 작품에 고객과의 깊은 대화를 담습니다.</p>
              <p>우리는 예술적 완성도와 의학적 안전성이 공존할 수 있다고 믿습니다. 그 믿음이 선율을 만들었고, 지금도 선율을 이끌어가고 있습니다.</p>
              <p>2018년 청담동의 작은 스튜디오에서 시작한 선율은, 이제 전국 30개 이상의 가맹점과 함께 한국 타투 문화의 새로운 기준을 만들어가고 있습니다.</p>
            </div>
            <div className="mt-6 sm:mt-8" style={{ opacity: visible ? 1 : 0, transition: 'opacity 1s ease 0.5s' }}>
              <TattooPathDrawing color="rgba(201,169,110,0.3)" width={160} height={110} />
            </div>
          </div>
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(40px)', transition: 'all 1s ease-out 0.2s' }}>
            <img
              src="https://readdy.ai/api/search-image?query=tattoo%20artist%20consulting%20with%20client%20in%20bright%20white%20minimalist%20luxury%20studio%20professional%20warm%20atmosphere%20natural%20daylight%20clean%20modern%20interior%20editorial%20photography%20style%20Seoul%20Korea&width=700&height=560&seq=about_philosophy1&orientation=landscape"
              alt="선율 철학"
              className="w-full h-[260px] sm:h-[340px] md:h-[420px] object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  const { ref, visible } = useScrollAnimation(0.08);
  return (
    <section className="py-16 sm:py-24 md:py-28 bg-[#FAFAFA] overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="bottom-right" color="rgba(201,169,110,0.1)" size={150} />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-16">
        <div className="text-center mb-8 sm:mb-10" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}>
          <p className="text-[9px] sm:text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-4 sm:mb-5">Core Values</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>선율이 추구하는 가치</h2>
        </div>
        <div className="mb-8 sm:mb-12">
          <GoldLineDivider delay={0.2} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {values.map((v, i) => (
            <div key={v.title} className="group bg-white border border-[#EBEBEB] p-6 sm:p-8 hover:border-[#C9A96E] transition-all duration-300 cursor-default"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s', transitionDelay: `${i * 100 + 200}ms` }}>
              <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center mb-5 sm:mb-6 border border-[#EBEBEB] group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-300">
                <i className={`${v.icon} text-base sm:text-lg text-[#AAAAAA] group-hover:text-white transition-colors duration-300`} />
              </div>
              <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2 sm:mb-3">{v.title}</h3>
              <p className="text-xs text-[#888] leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MilestoneSection() {
  const { ref, visible } = useScrollAnimation(0.05);
  return (
    <section className="py-16 sm:py-24 md:py-28 bg-white overflow-hidden relative" ref={ref}>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-16">
        <div className="text-center mb-10 sm:mb-12" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}>
          <p className="text-[9px] sm:text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-4 sm:mb-5">History</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#1A1A1A] mb-5 sm:mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>선율의 발자취</h2>
          <div className="flex justify-center" style={{ opacity: visible ? 1 : 0, transition: 'opacity 1s ease 0.3s' }}>
            <MandalaOrnament size={70} color="rgba(201,169,110,0.3)" delay={0.3} />
          </div>
        </div>
        <div className="relative">
          <div className="hidden md:block absolute left-[120px] top-0 bottom-0 w-px bg-[#EBEBEB]" />
          <div className="flex flex-col gap-0">
            {milestones.map((m, i) => (
              <div key={m.year} className="flex flex-col sm:flex-row gap-3 sm:gap-0 py-6 sm:py-8 border-b border-[#F5F5F5] last:border-0"
                style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.7s ease-out', transitionDelay: `${i * 100 + 200}ms` }}>
                <div className="sm:w-[120px] flex-shrink-0 flex sm:flex-col items-center sm:items-start gap-3 sm:gap-0">
                  <span
                    className="text-xl sm:text-2xl text-[#C9A96E]"
                    style={{ fontFamily: "'Cinzel', serif", fontWeight: 300, letterSpacing: '0.18em' }}
                  >{m.year}</span>
                  <div className="hidden md:block w-3 h-3 rounded-full bg-[#C9A96E] mt-2 ml-[-6px] relative z-10" />
                </div>
                <div className="sm:pl-12 flex-1">
                  <h3 className="text-sm font-semibold text-[#1A1A1A] mb-1.5 sm:mb-2">{m.title}</h3>
                  <p className="text-xs sm:text-sm text-[#888] leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutCta() {
  const { ref, visible } = useScrollAnimation(0.2);
  return (
    <section className="py-16 sm:py-20 md:py-24 bg-[#1A1A1A] relative overflow-hidden" ref={ref}>
      <FlowingWaves color="rgba(201,169,110,0.08)" lineCount={3} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.18)" size={150} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />
      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 md:px-16 text-center"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}>
        <p className="text-[9px] sm:text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5 sm:mb-6">Join Us</p>
        <h2 className="text-2xl sm:text-3xl md:text-4xl text-white mb-5 sm:mb-6 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
          선율의 이야기에<br /><span className="text-white/40">함께하세요.</span>
        </h2>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-8 sm:mt-10">
          <Link to="/franchise/guide" className="w-full sm:w-auto px-8 sm:px-10 py-3.5 sm:py-4 bg-[#C9A96E] text-white text-xs tracking-[0.2em] uppercase font-medium rounded-full hover:bg-[#b8955a] transition-colors duration-300 cursor-pointer whitespace-nowrap text-center">창업 안내 보기</Link>
          <Link to="/brand/safety" className="w-full sm:w-auto px-8 sm:px-10 py-3.5 sm:py-4 border border-white/20 text-white/60 text-xs tracking-[0.2em] uppercase font-medium rounded-full hover:border-white/50 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap text-center">위생 기준 보기</Link>
        </div>
      </div>
    </section>
  );
}

export default function BrandAboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <AboutHero />
        <BrandStorySection />
        <PhilosophySection />
        <ValuesSection />
        <MilestoneSection />
        <AboutCta />
      </main>
      <Footer />
    </div>
  );
}
